import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';

// P1 Görev Paketi B — İK "Sonuç İtirazları" kuyruğu. Sayfa adı BİLEREK
// "Sonuç İtirazları" (plan B): kalibrasyon el sıkışmasındaki itiraz NOTU
// (süreci durdurmayan, finalize öncesi) ile karışmasın — bu kuyruk dönem
// KAPANDIKTAN sonra açılan resmî itirazları listeler. Gerçek
// `ObjectionController` rotaları (controller ile birebir):
//   GET  v1/objections?status[in]=&id[eq]=&pageNumber=&pageSize=  ([ModuleAuthorize("PM","Read")]
//        + serviste enterpriseAdmin kapsamı — Görev 6/PMB-08: İK olmayan çağıran
//        yalnız KENDİ itirazını görür, kuyruk pratikte boş döner)
//   POST v1/objections/{id}/decide {decision, decisionNote, revisedFinalLevelId?}
//        ([ModuleAuthorize("PM","Write")] + serviste enterpriseAdmin şartı)
export const OBJECTION_STATUSES = ['open', 'accepted', 'rejected'];

// Rozet paleti — calibration/config CALIBRATION_STATUS_COLORS deseni.
export const OBJECTION_STATUS_COLORS = {
  open: { color: '#D97706', bg: '#FFFBEB' },
  accepted: { color: '#16A34A', bg: '#DCFCE7' },
  rejected: { color: '#DC2626', bg: '#FEF2F2' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// "Kalan gün" — WindowEndsAt tarihine (son gün DAHİL — BE ile aynı kapsayıcı
// kural) kalan takvim günü. Pencere geçmişse 0 döner; WindowEndsAt yoksa null.
export const remainingDays = (windowEndsAt) => {
  if (!windowEndsAt) return null;
  const end = new Date(windowEndsAt);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  end.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((end.getTime() - today.getTime()) / 86400000) + 1;
  return Math.max(0, diff);
};

// Analitik Plan 5 — kabul kalibrasyon kaydının nihai seviyesini değiştirir: kuyrukta okunan kayıt
// sürümü gönderilir (başkası değiştirdiyse sunucu 409 versionConflict döner).
export const buildDecidePayload = (decision, decisionNote, revisedFinalLevelId, expectedCalibrationVersion) => ({
  decision,
  decisionNote,
  revisedFinalLevelId: decision === 'accept' ? revisedFinalLevelId : undefined,
  expectedCalibrationVersion,
});

const useObjectionsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 20, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 20 };
    setFetching(true);
    api.get(`/v1/objections?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 20,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // Karar — hata mesajı YALNIZ axios interceptor'da (component'te toast yasak).
  // Plan 2 (Task 8) — revisedFinalLevelId dönemin skalasına ait AKTİF bir
  // seviyenin Guid'i (ObjectionQueueItemResponse.ScaleLevels'tan seçilir).
  const decide = useCallback(async (id, decision, decisionNote, revisedFinalLevelId, expectedCalibrationVersion) => {
    try {
      await api.post(`/v1/objections/${id}/decide`,
        buildDecidePayload(decision, decisionNote, revisedFinalLevelId, expectedCalibrationVersion));
      refresh();
      return true;
    } catch {
      refresh();
      return false;
    }
  }, [refresh]);

  // Gelen Kutum derin bağlantısı (?objectionId=, 2026-10-28) — kuyruğun bu sayfasında olmayan itirazın satırı AYNI liste
  // ucundan (id[eq] — kapsam ve kapılar aynı) tek kayıt olarak okunur. api.js GET'te reject ETMEZ: sinyal res.data.status.
  const findById = useCallback(async (id) => {
    try {
      const res = await api.get('/v1/objections', { params: { 'id[eq]': id, pageNumber: 1, pageSize: 1 } });
      if (!res?.data?.status) return null;
      const result = res.data.data;
      const items = Array.isArray(result) ? result : (result?.data || []);
      return items.find((o) => o.id === id) || null;
    } catch {
      return null;
    }
  }, []);

  const columns = [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.objections.col.employeeName'),
      options: { filter: false, sort: false },
    },
    {
      name: 'periodName',
      label: t('performance.objections.col.periodName'),
      options: { filter: false, sort: false },
    },
    {
      name: 'note',
      label: t('performance.objections.col.note'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v && v.length > 80 ? `${v.slice(0, 80)}…` : v),
      },
    },
    {
      name: 'status',
      label: t('performance.objections.col.status'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v) => {
          const cfg = OBJECTION_STATUS_COLORS[v] || OBJECTION_STATUS_COLORS.open;
          return (
            <StatusBadge color={cfg.color} bg={cfg.bg}>
              {t(`performance.objections.status.${v}`, { defaultValue: v })}
            </StatusBadge>
          );
        },
        customFilterListOptions: { render: (v) => t(`performance.objections.status.${v}`, { defaultValue: v }) },
        filterOptions: {
          defaultOp: 'in',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.objections.col.status')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>{t('performance.objections.filter.allStatuses')}</em></MenuItem>
                {OBJECTION_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{t(`performance.objections.status.${s}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'windowEndsAt',
      label: t('performance.objections.col.remainingDays'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          // Kalan gün yalnız AÇIK itirazlar için anlamlı; kararlılarda "—".
          const row = data[tableMeta.rowIndex];
          if (!row || row.status !== 'open') return '—';
          const days = remainingDays(v);
          if (days === null) return '—';
          if (days === 0) return t('performance.objections.windowExpired');
          return `${days} ${t('performance.objections.daysShort')}`;
        },
      },
    },
    {
      name: 'createDate',
      label: t('performance.objections.col.createDate'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) },
    },
  ];

  return {
    fetching, data, pagination, columns, decide, refresh, findById,
  };
};

export default useObjectionsApi;
